"use client";

import { useEffect, useMemo, useRef } from "react";

import { usePathname } from "next/navigation";

import { useTheme } from "@/contexts/theme-provider";

const DOT_COUNT = 50;
const MAX_CONNECTION_DISTANCE = 15; // in viewBox units (0-100)
const DOT_RADIUS = 1; // in viewBox units
const DOT_BLUR = 4; // in viewBox units

// Animation is driven entirely through refs + a canvas — no React state, so
// the rAF loop cannot cascade re-renders through the tree. That matters in
// dev, where reactCompiler runs in annotation mode and won't auto-memoize
// around a state-driven rAF loop (triggers "Maximum update depth" errors
// in upstream components).
function FloatingDots() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dots = Array.from({ length: DOT_COUNT }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      vx: (Math.random() - 0.5) * 0.008,
      vy: (Math.random() - 0.5) * 0.008,
    }));

    const rootStyle = getComputedStyle(document.documentElement);
    const primary =
      rootStyle.getPropertyValue("--primary").trim() || "99 102 241";
    const accent =
      rootStyle.getPropertyValue("--accent-foreground").trim() || primary;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
    };
    resize();
    window.addEventListener("resize", resize);

    let rafId: number | undefined;
    let mounted = true;

    const animate = () => {
      if (!mounted) return;
      const w = canvas.width;
      const h = canvas.height;
      const scaleX = w / 100;
      const scaleY = h / 100;

      for (const dot of dots) {
        dot.x = (dot.x + dot.vx + 100) % 100;
        dot.y = (dot.y + dot.vy + 100) % 100;
      }

      ctx.clearRect(0, 0, w, h);

      // Connection lines
      ctx.lineWidth = 0.15 * scaleX;
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i].x - dots[j].x;
          const dy = dots[i].y - dots[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance >= MAX_CONNECTION_DISTANCE) continue;
          const opacity =
            Math.max(0, 1 - distance / MAX_CONNECTION_DISTANCE) * 0.3;
          const x1 = dots[i].x * scaleX;
          const y1 = dots[i].y * scaleY;
          const x2 = dots[j].x * scaleX;
          const y2 = dots[j].y * scaleY;
          const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
          gradient.addColorStop(0, `rgb(${primary} / ${opacity * 0.6})`);
          gradient.addColorStop(1, `rgb(${accent} / ${opacity * 0.4})`);
          ctx.strokeStyle = gradient;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        }
      }

      // Dots
      ctx.fillStyle = `rgb(${primary} / 0.4)`;
      ctx.shadowColor = `rgb(${primary} / 0.5)`;
      ctx.shadowBlur = DOT_BLUR * scaleX;
      const radius = DOT_RADIUS * scaleX;
      for (const dot of dots) {
        ctx.beginPath();
        ctx.arc(dot.x * scaleX, dot.y * scaleY, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;

      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      mounted = false;
      window.removeEventListener("resize", resize);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 -z-10 h-full w-full"
      aria-hidden="true"
    />
  );
}

function BackgroundPattern() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <>
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage: isDark
            ? `
            linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
          `
            : `
            linear-gradient(to right, rgba(0, 0, 0, 0.08) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 0, 0, 0.08) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: isDark
            ? `
            linear-gradient(to right, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
          `
            : `
            linear-gradient(to right, rgba(0, 0, 0, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: "20px 20px",
        }}
      />
    </>
  );
}

export const Background = ({
  showDots = false,
}: {
  animate?: boolean;
  showDots?: boolean;
}) => {
  const pathname = usePathname();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const dotsEnabled = useMemo(
    () =>
      pathname.includes("/login") ||
      pathname.includes("/reset-password") ||
      pathname.includes("/patient-portal") ||
      showDots,
    [pathname, showDots],
  );

  return (
    <>
      {dotsEnabled && <FloatingDots />}
      <div className="absolute inset-0 bg-background" />
      {isDark ? (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgb(var(--accent)/0.35),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgb(var(--foreground-secondary)/0.12),transparent_45%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_55%_10%,rgb(var(--border-secondary)/0.15),transparent_35%)]" />
        </>
      ) : (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgb(var(--border-secondary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgb(var(--foreground-secondary)/0.15),transparent_50%)]" />
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[rgb(var(--primary)/0.2)]" />
        </>
      )}
      <BackgroundPattern />
    </>
  );
};
